import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';

import { ProjectLocationModel } from '../../models/project-location.model';
import { ProjectLocationActions } from './project-location.actions';

export interface ProjectLocationState
  extends EntityState<ProjectLocationModel.LocationDocument> {
  isLoading: boolean;
  hasLoaded: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  deletingLocationIds: string[];
}

export const projectLocationAdapter: EntityAdapter<ProjectLocationModel.LocationDocument> =
  createEntityAdapter<ProjectLocationModel.LocationDocument>({
    selectId: (location) => location._id,
  });

const initialState: ProjectLocationState = projectLocationAdapter.getInitialState(
  {
    isLoading: false,
    hasLoaded: false,
    isCreating: false,
    isUpdating: false,
    deletingLocationIds: [],
  },
);

export const projectLocationStateReducer = createReducer(
  initialState,
  on(ProjectLocationActions.resetToInitialState, () => {
    return {
      ...initialState,
    };
  }),
  on(ProjectLocationActions.loadLocations, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(
    ProjectLocationActions.loadLocationsSuccess,
    (state, { locations }) => {
      return projectLocationAdapter.setAll(locations, {
        ...state,
        isLoading: false,
        hasLoaded: true,
      });
    },
  ),
  on(ProjectLocationActions.loadLocationsFail, (state) => {
    return {
      ...state,
      isLoading: false,
      hasLoaded: true,
    };
  }),

  on(ProjectLocationActions.createLocation, (state) => {
    return {
      ...state,
      isCreating: true,
    };
  }),
  on(
    ProjectLocationActions.createLocationSuccess,
    (state, { location }) => {
      return projectLocationAdapter.upsertOne({
        ...location,
        totalPrice: location.totalPrice ?? 0,
      }, {
        ...state,
        isCreating: false,
      });
    },
  ),
  on(ProjectLocationActions.createLocationFail, (state) => {
    return {
      ...state,
      isCreating: false,
    };
  }),

  on(ProjectLocationActions.updateLocation, (state) => {
    return {
      ...state,
      isUpdating: true,
    };
  }),
  on(
    ProjectLocationActions.updateLocationSuccess,
    (state, { location }) => {
      return projectLocationAdapter.upsertOne({
        ...location,
        totalPrice: location.totalPrice ?? 0,
      }, {
        ...state,
        isUpdating: false,
      });
    },
  ),
  on(ProjectLocationActions.updateLocationFail, (state) => {
    return {
      ...state,
      isUpdating: false,
    };
  }),

  on(ProjectLocationActions.deleteLocation, (state, { locationId }) => {
    return {
      ...state,
      deletingLocationIds: state.deletingLocationIds.includes(locationId)
        ? state.deletingLocationIds
        : [...state.deletingLocationIds, locationId],
    };
  }),
  on(ProjectLocationActions.deleteLocationSuccess, (state, { locationId }) => {
    return projectLocationAdapter.removeOne(locationId, {
      ...state,
      deletingLocationIds: state.deletingLocationIds.filter(
        (id) => id !== locationId,
      ),
    });
  }),
  on(ProjectLocationActions.deleteLocationFail, (state, { locationId }) => {
    return {
      ...state,
      deletingLocationIds: state.deletingLocationIds.filter(
        (id) => id !== locationId,
      ),
    };
  }),
);

export const projectLocationFeature = createFeature({
  name: 'projectLocation',
  reducer: projectLocationStateReducer,
  extraSelectors: ({ selectProjectLocationState }) => {
    const { selectAll, selectEntities } =
      projectLocationAdapter.getSelectors(selectProjectLocationState);

    return {
      selectProjectLocationEntities: selectEntities,
      selectProjectLocations: selectAll,
      selectProjectLocationById: (locationId: string) =>
        createSelector(selectEntities, (entities) => {
          return entities[locationId] ?? null;
        }),
    };
  },
});
